import React, { createContext, useContext, useEffect, useState } from "react";
import { authService } from "../services/shared/auth";
import {
  AuthContextType,
  AuthResponse,
  LoginCredentials,
  RegisterData,
  User,
} from "../types/auth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay un token guardado al cargar la aplicación
    const token = localStorage.getItem("accessToken");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        authService.setToken(token);
      } catch (error) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("user");
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);

      if (response.status === "success" && response.data) {
        if (!response.data.accessToken) {
          throw new Error("El servidor no devolvió un token de acceso");
        }

        setUser(response.data.usuario);
        localStorage.setItem("accessToken", response.data.accessToken);
        localStorage.setItem(
          "refreshToken",
          response.data.refreshToken || response.data.accessToken
        );
        localStorage.setItem("user", JSON.stringify(response.data.usuario));

        return response;
      } else {
        throw new Error(response.message || "Error en el login");
      }
    } catch (error: any) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    authService.clearToken();
  };

  const register = async (data: RegisterData): Promise<AuthResponse> => {
    try {
      const response = await authService.register(data);
      if (response.status === "success" && response.data) {
        setUser(response.data.usuario);
        localStorage.setItem("accessToken", response.data.accessToken);
        localStorage.setItem("refreshToken", response.data.refreshToken);
        localStorage.setItem("user", JSON.stringify(response.data.usuario));
      }
      return response;
    } catch (error: any) {
      // Si hay un error, devolver una respuesta de error
      return {
        status: "error",
        message: error.message || "Error en el registro",
      };
    }
  };

  const refreshToken = async () => {
    try {
      const response = await authService.refreshToken();
      if (response.status === "success" && response.data) {
        setUser(response.data.usuario);
        localStorage.setItem("accessToken", response.data.accessToken);
        localStorage.setItem("refreshToken", response.data.refreshToken);
        localStorage.setItem("user", JSON.stringify(response.data.usuario));
      } else {
        logout();
      }
    } catch (error) {
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    register,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
